import { FunctionComponent, useEffect, useState } from 'react';
import { Typography } from '@mui/material';
import { DatePicker } from '@/components/DatePicker/DatePicker';
import { FlexContainer } from '@/components/Layout/Container';
import { DateRange } from '@/models/general';
import { getYear, subYears } from 'date-fns';

const reduceYears = (value: Date) => {
    if (!value) return null;
    const year = getYear(value);

    if (year > 2099) {
        return subYears(value, year - 2099);
    }

    return value;
};

const normalizeDateRange = ([from, to]: DateRange): DateRange => [
    reduceYears(from),
    reduceYears(to),
];

type DateRangePickerDesktopProps = {
    onChange: (date: DateRange) => void;
    rangeDelimiter?: string;
    fromText: string;
    toText: string;
    title?: string;
    value?: DateRange;
};

export const DateRangePicker: FunctionComponent<
    DateRangePickerDesktopProps
> = ({
    onChange,
    title,
    fromText = 'von',
    toText = 'to',
    rangeDelimiter = '-',
    value = [null, null],
}) => {
    const [selectedDate, setSelectedDate] = useState<DateRange>(
        normalizeDateRange(value)
    );

    useEffect(() => {
        setSelectedDate(normalizeDateRange(value));
    }, [...value]);

    const handleChange = (type: 'from' | 'to') => (date: Date) => {
        let copyDate: DateRange = [...selectedDate];

        if (type === 'from') {
            copyDate = [date, copyDate[1]];
        } else {
            copyDate = [copyDate[0], date];
        }

        setSelectedDate(copyDate);
        onChange(copyDate);
    };

    return (
        <FlexContainer
            sx={{
                flexDirection: 'column',
            }}
        >
            {title && (
                <Typography
                    sx={{
                        mb: 2,
                    }}
                    variant='h6'
                >
                    {title}
                </Typography>
            )}

            <FlexContainer
                sx={{
                    alignItems: 'center',
                }}
            >
                <FlexContainer
                    sx={{
                        flex: 1,
                    }}
                >
                    <DatePicker
                        label={fromText}
                        onChange={handleChange('from')}
                        value={selectedDate[0]}
                    />
                </FlexContainer>

                <FlexContainer
                    sx={{
                        width: '24px',
                    }}
                    center
                >
                    {rangeDelimiter}
                </FlexContainer>
                <FlexContainer
                    sx={{
                        flex: 1,
                    }}
                >
                    <DatePicker
                        label={toText}
                        onChange={handleChange('to')}
                        value={selectedDate[1]}
                    />
                </FlexContainer>
            </FlexContainer>
        </FlexContainer>
    );
};
